import { useState } from 'react';

import { Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from '@mui/material';

import { PageLoader } from '@/components/common';

import { useWizardMetadata } from '../../hooks';

interface MetadataObject {
  name: string;
  label: string;
  type?: string;
}

interface WizardSchemaStepProps {
  connectionKey: string;
  providerType: string;
  showApiNames: boolean;
  onNext: (selectedObjects: string[]) => void;
  onSaveAndExit?: () => void;
  initialSelected?: string[];
}

export const WizardSchemaStep = ({
  connectionKey,
  providerType,
  showApiNames,
  onNext,
  onSaveAndExit,
  initialSelected = [],
}: WizardSchemaStepProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState<string[]>(initialSelected);

  // Use wizard metadata hook with provider-specific transformation
  const { metadata, isLoading, error } = useWizardMetadata(
    connectionKey,
    providerType,
    !connectionKey
  );

  const objects = (metadata?.objects || []) as MetadataObject[];

  const filteredObjects = objects.filter(
    (obj) =>
      obj.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      obj.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggle = (objectName: string) => {
    setSelected((prev) =>
      prev.includes(objectName) ? prev.filter((o) => o !== objectName) : [...prev, objectName]
    );
  };

  const handleSelectAll = () => {
    if (selected.length === filteredObjects.length) {
      setSelected([]);
    } else {
      setSelected(filteredObjects.map((o) => o.name));
    }
  };

  const handleNext = () => {
    if (selected.length > 0) {
      onNext(selected);
    }
  };

  if (isLoading) {
    return <PageLoader variant="section" />;
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Failed to load metadata. Please try again.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Select Objects to Sync
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose which objects you want to synchronize
      </Typography>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            placeholder="Search objects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />
        </Grid>

        <Grid item xs={12}>
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
          >
            <Typography variant="body2" color="text.secondary">
              {selected.length} of {filteredObjects.length} selected
            </Typography>
            <Button size="small" onClick={handleSelectAll}>
              {selected.length === filteredObjects.length ? 'Deselect All' : 'Select All'}
            </Button>
          </Box>

          <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
            <List>
              {filteredObjects.map((obj) => (
                <ListItem key={obj.name} disablePadding>
                  <ListItemButton onClick={() => handleToggle(obj.name)} dense>
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selected.includes(obj.name)}
                        tabIndex={-1}
                        disableRipple
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={showApiNames ? obj.name : obj.label}
                      secondary={showApiNames ? obj.label : obj.name}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, display: 'flex', gap: 1 }}>
        <Button variant="contained" onClick={handleNext} disabled={selected.length === 0}>
          Next
        </Button>
        {onSaveAndExit && (
          <Button variant="outlined" onClick={onSaveAndExit}>
            Save and Exit
          </Button>
        )}
      </Box>
    </Box>
  );
};
